import React, { useState } from 'react';
import Typography from '../components/v2/Typography';
import Button from '../components/v2/Button';
import { Download, ArrowRight, Search, Menu, SearchIcon, Globe, InfoIcon, CheckIcon } from 'lucide-react';
import DomainCard from '../components/v2/DomainCard';
import IButton from '../components/v2/IButton';
import Input from '../components/v2/Input';
import Textarea from '../components/v2/TextArea';
import { Link } from 'react-router-dom';
import Faq, { faqData } from '../atoms/Faq';
import BlogSection from '../atoms/BlogPreview';
import ContactSection from '../atoms/Contact';
import useApp from '../hooks/useApp';


export const WHOISDomainToolAdvert = ({separator}) => {
    
    return (

        <div className='tool-advert-wrapper' style={{borderTop: separator ? '1px solid #e5e7eb' : 'none'}}>
            <div className="inner">

                <div className="section section-left">
                    <div className="section-text">
                        <Typography variant="para">WHOIS tool</Typography>
                        <Typography variant="h1">Find out important details<br />about the domain.</Typography>
                    </div>

                    <div className="section-controls">
                        <a href='/tool/whois'><Button variant="secondary">Find WHOIS information</Button></a>
                    </div>
                </div>

            </div>
        </div>

    );
}


const ToolHero = () => {

    const appCtx = useApp();

    const findWHOIS = async () => {
        const domain = document.getElementById('whois-domain').value;
        // appCtx.fetchWhois(domain);\

        await appCtx.fetchDomainValuation(domain);
    }

    const handleContact = (e) => {
        
    }
    
    return (
        <section className="tool-hero-wrapper">
            <div className="inner">
                <div className="hero-tabs">
                    <a href='/tool/whois'>
                        <Typography variant="button">WHOIS Tool</Typography>
                    </a>
                    <a href='/tool/appraisal'>
                        <Typography variant="button">Domain Valuation Tool</Typography>
                    </a>
                </div>
                <div className="hero-content-wrapper">
                    <div className='hero-text-content-wrapper'>
                        <div className="hero-text-wrapper">
                            <Typography variant="h1">Value Your Domain</Typography>
                            <Typography variant={'para'}>Make smart domain investment decisions using our AI-powered valuation system.</Typography>
                        </div>
                        <div className="hero-controls-wrapper">
                            {/* Add buttons or controls here */}
                            <div className="find-tool">
                                <div className="find-inner">
                                    <div className="find-input">
                                        <input autoFocus={true} id="whois-domain" type="text" placeholder="Enter a domain name to estimate" />
                                    </div>
                                    <div className="find-button">
                                        <Button variant='primary' onClick={findWHOIS}>Estimate</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="hero-ft-text-wrapper">
                            <Typography variant="info">Looking for a professional valuation? <a href='/services/domain-appraisals'>View Appraisal Services</a></Typography>
                        </div>
                    </div>
                </div>

                {appCtx.domainValuation && <div className="hero-results">
                    <div className="hero-result-card">
                        <div className="left">
                            <div className="hdr">
                                <Typography variant="p">Auction Value</Typography>
                            </div>
                            <div className="info">
                                <Typography variant="info">Estimated value if sold via a well-publicized domain auction with competitive bidding. Assumes a large buyer pool and active promotion.</Typography>
                            </div>
                        </div>
                        <div className="right">
                            <div className="val">
                                <Typography variant="h1">${appCtx.domainValuation.auction}</Typography>
                            </div>
                        </div>
                    </div>
                    <div className="hero-result-card">
                        <div className="left">
                            <div className="hdr">
                                <Typography variant="p">Marketplace Value</Typography>
                            </div>
                            <div className="info">
                                <Typography variant="info">Estimated value if sold on a domain marketplace with standard listing fees. Assumes moderate visibility and a targeted buyer audience.</Typography>
                            </div>
                        </div>
                        <div className="right">
                            <div className="val">
                                <Typography variant="h1">${appCtx.domainValuation.marketplace}</Typography>
                            </div>
                        </div>
                    </div>
                    <div className="hero-result-card">
                        <div className="left">
                            <div className="hdr">
                                <Typography variant="p">Brokered Sale Value</Typography>
                            </div>
                            <div className="info">
                                <Typography variant="info">Estimated value if sold through a domain broker with personalized negotiation. Assumes expert representation and a premium buyer network, often with higher commissions.</Typography>
                            </div>
                        </div>
                        <div className="right">
                            <div className="val">
                                <Typography variant="h1">${appCtx.domainValuation.brokerage}</Typography>
                            </div>
                        </div>
                    </div>
                    <div className="hero-sugg">
                        <Button onClick={handleContact} variant="primary">Interested in this domain? Contact Us</Button>
                    </div>
                </div>}

                <div className='hero-footer'>
                    <div className='hero-bg-separator-new'></div>
                    <Typography variant="info">Valuations are estimates based on AI models analyzing historical sales data and domain characteristics. Accuracy is highest for common TLDs (.com, .net, .org) and may vary for others. Market conditions and buyer specifics can significantly influence actual sale prices.</Typography>
                </div>

            </div>
        </section>
    );
}


/**
 * Home page component with feed
 * @returns {JSX.Element} Home page component
 */
const Tools = () => {

    const {allFaqList} = useApp();

    return (
        <>
            <ToolHero />
            
            {/* Why Trust NameConnect */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why Trust NameConnect?</h2>
                    <p className="text-lg">Our team has brokered over eight figures in domain name sales, placing names like <strong>CTO.com</strong> and <strong>League.com</strong>. We combine deep market insight with trusted processes to deliver credible domain valuations you can act on.</p>
                </div>
            </section>

            {/* Professional Appraisal Tiers */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Professional Appraisal Tiers</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-4">Standard – $99</h3>
                            <ul className="list-disc list-inside space-y-2 text-sm">
                                <li>3-page PDF within 48 hours</li>
                                <li>Verified comparable sales</li>
                                <li>SEO & brandability metrics</li>
                                <li>Recommended pricing range</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-4">Premium – $1,500</h3>
                            <ul className="list-disc list-inside space-y-2 text-sm">
                                <li>10-page report in 5 days</li>
                                <li>Buyer segmentation & liquidity score</li>
                                <li>Sale-probability analysis</li>
                                <li>15-min consultation included</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-4">Portfolio – Custom</h3>
                            <ul className="list-disc list-inside space-y-2 text-sm">
                                <li>For 20+ domains</li>
                                <li>Ideal for corporate/investor portfolios</li>
                                <li>Custom quote & delivery timeline</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* What's Included */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">What's Included in a NameConnect Appraisal</h2>
                    <ul className="list-disc list-inside space-y-3 text-lg">
                        <li>10+ verified comparable sales</li>
                        <li>Brand strength & SEO performance</li>
                        <li>Market category & liquidity profile</li>
                        <li>Price guidance + positioning strategy</li>
                    </ul>
                </div>
            </section>

            {/* Important Disclaimers */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Important Disclaimers</h2>
                    <p className="text-sm text-gray-600">Appraisals reflect estimated value ranges based on historical data and market signals. Final sale outcomes can vary ±30% depending on buyer timing and interest. Professional appraisals do not guarantee brokerage acceptance or pricing.</p>
                </div>
            </section>

            {/* Custom FAQ Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">How accurate is the free tool?</summary>
                            <p className="pt-2">It offers a fast benchmark using AI-trained models. For serious valuation, we recommend a human-led report.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Can I use your appraisal to sell the domain?</summary>
                            <p className="pt-2">Yes. Many clients use it to set reserve pricing or justify sales strategy.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Is the valuation confidential?</summary>
                            <p className="pt-2">Yes. All reports are delivered securely and privately. For portfolio valuations, we use NDAs when appropriate.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Can I upgrade to brokerage afterward?</summary>
                            <p className="pt-2">We may invite eligible domains into our brokerage program after review. We reserve the right to decline brokerage services but will consider all submissions.</p>
                        </details>
                    </div>
                </div>
            </section>

            <WHOISDomainToolAdvert separator={true} />


            {/* CTA Section */}
            <section className="py-16 bg-blue-600 text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold mb-4">Need a Professional Appraisal?</h2>
                    <p className="text-lg mb-8">Get detailed insights and expert analysis for your high-value domains.</p>
                    <a href="/services/domain-appraisals" className="inline-block bg-white text-blue-600 font-semibold py-4 px-8 rounded">Explore Full Appraisal Services</a>
                </div>
            </section>

            {/* <Faq data={allFaqList["tool/appraisal"]} /> */}
            <ContactSection />
            <BlogSection />
        </>
    );
};

export default Tools;